import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, fmtTime, timeAgo } from "@/lib/format";
import { printInvoice, printKOT, printOrderJobs } from "@/lib/print";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Printer, ChefHat, RefreshCw, Bike } from "lucide-react";

const STATUS_FLOW = ["pending", "preparing", "ready", "completed"];
const STATUS_COLOR = {
  pending: "bg-neutral-100 text-neutral-700",
  preparing: "bg-yellow-100 text-yellow-800",
  ready: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};
const CHANNEL = {
  zomato: { label: "Zomato", cls: "bg-red-100 text-red-700" },
  swiggy: { label: "Swiggy", cls: "bg-orange-100 text-orange-700" },
  online: { label: "Online", cls: "bg-emerald-100 text-emerald-700" },
  pos: { label: "POS", cls: "bg-neutral-100 text-neutral-600" },
};
const FILTERS = ["all", "pending", "preparing", "ready", "completed"];

export default function Orders() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [pulling, setPulling] = useState(false);
  const { data: orders = [] } = useQuery({ queryKey: ["orders"], queryFn: async () => (await api.get("/orders")).data, refetchInterval: 10000 });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const sym = settings?.currency_symbol || "₹";

  const list = orders.filter((o) => filter === "all" || o.status === filter);

  const advance = async (o) => {
    const next = STATUS_FLOW[Math.min(STATUS_FLOW.indexOf(o.status) + 1, 3)];
    await api.put(`/orders/${o.id}`, { status: next });
    toast.success(`Order ${o.order_number} → ${next}`);
    qc.invalidateQueries({ queryKey: ["orders"] });
  };
  const voidOrder = async (o) => {
    const reason = window.prompt("Void reason (required):");
    if (reason == null) return;
    if (!String(reason).trim()) return toast.error("Reason is required");
    try {
      await api.post(`/orders/${o.id}/void`, { reason: String(reason).trim() });
      toast.success(`Voided ${o.order_number}`);
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Void failed");
    }
  };
  const pull = async () => {
    setPulling(true);
    try {
      const { data } = await api.post("/integrations/pull-delivery");
      toast.success(`Pulled ${data.pulled} delivery order(s)`);
      qc.invalidateQueries({ queryKey: ["orders"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Connect a channel first"); }
    finally { setPulling(false); }
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Orders" subtitle="Live kitchen & multi-channel order tracking">
        <Button variant="outline" onClick={pull} disabled={pulling} data-testid="pull-delivery-btn">
          <Bike className={`w-4 h-4 mr-1 ${pulling ? "animate-pulse" : ""}`} /> Pull Delivery Orders
        </Button>
      </PageHeader>

      <div className="flex gap-1 flex-wrap mb-5">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`order-filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-sm capitalize transition-colors ${filter === f ? "bg-[var(--kul-charcoal)] text-white" : "bg-white border text-neutral-600"}`}>{f}</button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((o) => {
          const ch = CHANNEL[o.channel] || CHANNEL.pos;
          return (
            <div key={o.id} className="kul-card p-4" data-testid={`order-card-${o.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono-k font-semibold">{o.order_number}</span>
                  <Badge variant="outline" className="capitalize text-xs">{o.order_type}</Badge>
                  <Badge className={`text-xs ${ch.cls}`}>{ch.label}</Badge>
                </div>
                <Badge className={`capitalize ${STATUS_COLOR[o.status] || ""}`}>{o.status}</Badge>
              </div>
              <div className="text-xs text-neutral-400 mt-1">{fmtTime(o.created_at)} · {timeAgo(o.created_at)} {o.customer_name ? `· ${o.customer_name}` : ""}</div>
              <div className="mt-3 space-y-1 text-sm">
                {o.items.map((it, i) => (<div key={i} className="flex justify-between"><span>{it.qty}× {it.name}</span><span className="font-mono-k text-neutral-500">{money(it.price * it.qty, sym)}</span></div>))}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <div>
                  <span className="font-display font-800 text-lg">{money(o.total, sym)}</span>
                  <Badge className={`ml-2 text-xs ${o.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-neutral-100 text-neutral-600"}`}>{o.payment_status}</Badge>
                </div>
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => printOrderJobs(o.id, "kot", { settings }).catch(() => printKOT(o))} data-testid={`kot-${o.id}`}><ChefHat className="w-3.5 h-3.5 mr-1" />KOT</Button>
                <Button size="sm" variant="outline" onClick={() => printOrderJobs(o.id, "invoice", { settings }).catch(() => printInvoice(o, settings))} data-testid={`invoice-${o.id}`}><Printer className="w-3.5 h-3.5 mr-1" />Invoice</Button>
                {o.status !== "cancelled" && (
                  <Button size="sm" variant="ghost" onClick={() => voidOrder(o)} className="text-[var(--kul-danger)]" data-testid={`void-${o.id}`}>Void</Button>
                )}
                {!["completed", "cancelled"].includes(o.status) && (
                  <Button size="sm" onClick={() => advance(o)} data-testid={`advance-${o.id}`} className="bg-[var(--kul-primary)] hover:bg-[var(--kul-primary-hover)] text-white capitalize ml-auto">
                    {STATUS_FLOW[Math.min(STATUS_FLOW.indexOf(o.status) + 1, 3)]}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {!list.length && <p className="text-neutral-400 col-span-full text-center py-16">No orders here.</p>}
      </div>
    </div>
  );
}
